import React, { useCallback, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { LoginType, useSendSecureCodeMutation } from '@websky/graphql';
import { useLoginFormError } from '../../../hooks';
import FirstLoginAgreementBase from '../FirstLoginAgreement/FirstLoginAgreement';
import { useRenderers } from '../../../../renderProps';
import { useConfig } from '../../../../context';
import { isEmailDomainBlockedForRegistration } from '../../../utils';

interface Props {
	login: string;
	loginType: LoginType;
	onSuccess: () => void;
	onError: (error: string) => void;
}

const RegistrationForm: FC<Props> = ({ login, loginType, onSuccess, onError }) => {
	const [sendCode, { loading }] = useSendSecureCodeMutation();

	const { t } = useTranslation('LoginPage');
	const blockedRegistrationEmailDomains = useConfig().global.authMethods?.blockedRegistrationEmailDomains;

	const { setError, setDefaultError } = useLoginFormError(onError);

	const { FirstLoginAgreement: FirstLoginAgreementOverride = FirstLoginAgreementBase } = useRenderers();

	const onSubmit = useCallback(async () => {
		if (loginType === LoginType.FFP) {
			onSuccess();
		} else {
			if (
				loginType === LoginType.Email &&
				isEmailDomainBlockedForRegistration(login, blockedRegistrationEmailDomains)
			) {
				onError(t('Registration with this email domain is forbidden'));

				return;
			}

			try {
				const data = await sendCode({
					variables: {
						params: {
							login,
							loginType
						}
					}
				});

				if (data.data.SendSecureCode.result) {
					onSuccess();
				} else {
					setDefaultError();
				}
			} catch (e) {
				setError(e);
			}
		}
	}, [onSuccess, onError, login, loginType, t]);

	return <FirstLoginAgreementOverride onConfirm={onSubmit} loading={loading} />;
};

export default RegistrationForm;
